import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, FileText, Camera, Loader2, Trash2, MapPin, X, Navigation, Check } from "lucide-react";
import { queueItem } from "@/lib/offlineQueue";
import { useOfflineSync } from "@/hooks/useOfflineSync";
import OfflineBadge from "@/components/OfflineBadge";

const CATEGORIES = {
  note: { label: "Note", color: "bg-slate-100 text-slate-700" },
  incident: { label: "Incident", color: "bg-red-100 text-red-700" },
  observation: { label: "Observation", color: "bg-blue-100 text-blue-700" },
  journal: { label: "Journal de bord", color: "bg-amber-100 text-amber-700" },
};

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = { titre: "", corps: "", categorie: "note", lieu: "", mission_id: "" };

export default function NotesTerrain() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [viewPhoto, setViewPhoto] = useState(null);
  const [gps, setGps] = useState(null); // {lat, lng, accuracy}
  const [geoLoading, setGeoLoading] = useState(false);
  const fileRef = useRef(null);

  const captureGps = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation non supportée"); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy) });
        setGeoLoading(false);
        toast.success(`Position captée (±${Math.round(pos.coords.accuracy)} m)`);
      },
      (err) => {
        setGeoLoading(false);
        toast.error(`Géolocalisation refusée (${err.message})`);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/notes-terrain"); setItems(data || []); }
    catch { /* ok */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useOfflineSync({ onSynced: () => load() });

  const onPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Photo trop volumineuse (max 10 Mo)"); return; }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const clearPhoto = () => { setPhotoFile(null); setPhotoPreview(null); if (fileRef.current) fileRef.current.value = ""; };
  const clearGps = () => setGps(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.titre.trim()) return toast.error("Titre requis");
    setSubmitting(true);
    try {
      if (!navigator.onLine) {
        // Queue avec photo en base64
        await queueItem("pending_notes", {
          __multipart: true,
          titre: form.titre, corps: form.corps, categorie: form.categorie,
          lieu: form.lieu || undefined, mission_id: form.mission_id || undefined,
          latitude: gps?.lat, longitude: gps?.lng, location_accuracy: gps?.accuracy,
          photo_data: photoPreview || undefined,
        });
        toast.success("Hors-ligne : note enregistrée localement");
        setOpen(false); setForm(EMPTY); clearPhoto(); clearGps();
        return;
      }
      const fd = new FormData();
      fd.append("titre", form.titre);
      fd.append("corps", form.corps);
      fd.append("categorie", form.categorie);
      if (form.lieu) fd.append("lieu", form.lieu);
      if (form.mission_id) fd.append("mission_id", form.mission_id);
      if (gps) {
        fd.append("latitude", String(gps.lat));
        fd.append("longitude", String(gps.lng));
        if (gps.accuracy) fd.append("location_accuracy", String(gps.accuracy));
      }
      if (photoFile) fd.append("photo", photoFile);
      await api.post("/notes-terrain", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Note créée");
      setOpen(false); setForm(EMPTY); clearPhoto(); clearGps(); load();
    } catch (err) {
      if (!err.response) {
        try {
          await queueItem("pending_notes", {
            __multipart: true,
            titre: form.titre, corps: form.corps, categorie: form.categorie,
            lieu: form.lieu || undefined, mission_id: form.mission_id || undefined,
            latitude: gps?.lat, longitude: gps?.lng, location_accuracy: gps?.accuracy,
            photo_data: photoPreview || undefined,
          });
          toast.success("Réseau instable : note enregistrée localement");
          setOpen(false); setForm(EMPTY); clearPhoto(); clearGps();
          return;
        } catch {}
      }
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const deleteNote = async (id) => {
    if (!window.confirm("Supprimer cette note ?")) return;
    try { await api.delete(`/notes-terrain/${id}`); toast.success("Supprimée"); load(); }
    catch { toast.error("Suppression impossible"); }
  };

  const photoUrl = (nid) => {
    const token = localStorage.getItem("oscopge_token");
    return `${BASE_URL}/api/notes-terrain/${nid}/photo?t=${token?.slice(-10)}`;
  };

  return (
    <div data-testid="notes-terrain-page">
      <PageHeader
        title="Notes de terrain & Journal de bord"
        subtitle="Prenez des notes, consignez incidents et observations avec photo depuis le terrain — fonctionne hors-ligne."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <OfflineBadge />
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) clearPhoto(); }}>
              <DialogTrigger asChild>
                <Button data-testid="notes-add-btn" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                  <Plus className="h-4 w-4 mr-2" /> Nouvelle note
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Nouvelle note de terrain</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <Label>Catégorie</Label>
                    <Select value={form.categorie} onValueChange={(v) => setForm({ ...form, categorie: v })}>
                      <SelectTrigger data-testid="notes-categorie" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Titre *</Label>
                    <Input value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} data-testid="notes-titre" className="mt-1" placeholder="Résumé en une ligne" required />
                  </div>
                  <div>
                    <Label>Détails</Label>
                    <Textarea rows={5} value={form.corps} onChange={(e) => setForm({ ...form, corps: e.target.value })} data-testid="notes-corps" className="mt-1" placeholder="Décrivez la situation, l'observation, l'incident…" />
                  </div>
                  <div>
                    <Label>Lieu</Label>
                    <Input value={form.lieu} onChange={(e) => setForm({ ...form, lieu: e.target.value })} className="mt-1" placeholder="Site, adresse, ville…" />
                  </div>
                  <div>
                    <Label>Position GPS (optionnelle)</Label>
                    {gps ? (
                      <div className="mt-1 flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-xs text-emerald-800">
                          <Check className="h-4 w-4" />
                          <div>
                            <div className="font-mono">{gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}</div>
                            <div className="text-emerald-600">Précision ±{gps.accuracy} m</div>
                          </div>
                        </div>
                        <button type="button" onClick={clearGps} className="text-emerald-700 hover:text-red-600"><X className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      <button type="button" onClick={captureGps} disabled={geoLoading} data-testid="notes-gps-btn" className="mt-1 w-full h-11 border border-slate-300 hover:border-[#C5A059] hover:bg-[#FDF9F1] rounded-md flex items-center justify-center gap-2 text-sm text-slate-700 transition-colors">
                        {geoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}
                        {geoLoading ? "Localisation en cours…" : "Capturer ma position"}
                      </button>
                    )}
                  </div>
                  <div>
                    <Label>Photo (optionnelle, max 10 Mo)</Label>
                    <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPhotoChange} className="hidden" data-testid="notes-photo-input" />
                    {!photoPreview ? (
                      <button type="button" onClick={() => fileRef.current?.click()} className="mt-1 w-full h-32 border-2 border-dashed border-slate-300 hover:border-[#C5A059] rounded-lg flex flex-col items-center justify-center text-slate-500 hover:text-[#A88846] transition-colors" data-testid="notes-photo-btn">
                        <Camera className="h-6 w-6 mb-1" />
                        <span className="text-xs">Cliquer ou prendre une photo</span>
                      </button>
                    ) : (
                      <div className="mt-1 relative">
                        <img src={photoPreview} alt="Aperçu" className="w-full max-h-56 object-cover rounded-lg" />
                        <button type="button" onClick={clearPhoto} className="absolute top-2 right-2 bg-red-500 text-white h-7 w-7 rounded-full flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={submitting} data-testid="notes-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Enregistrer
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <FileText className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">Aucune note pour le moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((n) => {
            const cat = CATEGORIES[n.categorie] || CATEGORIES.note;
            return (
              <div key={n.id} data-testid={`note-card-${n.id}`} className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col">
                {n.photo && (
                  <button onClick={() => setViewPhoto(photoUrl(n.id))} className="h-40 bg-slate-100 overflow-hidden">
                    <img src={photoUrl(n.id)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                  </button>
                )}
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${cat.color}`}>{cat.label}</span>
                      <h3 className="font-semibold text-slate-900 text-sm mt-1 truncate">{n.titre}</h3>
                    </div>
                    <button onClick={() => deleteNote(n.id)} data-testid={`note-delete-${n.id}`} className="text-slate-300 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                  {n.corps && <p className="text-xs text-slate-600 mt-2 line-clamp-4 whitespace-pre-wrap">{n.corps}</p>}
                  <div className="mt-auto pt-3 flex items-center justify-between text-[10px] text-slate-400">
                    <span>{n.author_name || n.author_email}</span>
                    <span>{new Date(n.created_at).toLocaleDateString("fr-FR")}</span>
                  </div>
                  {n.lieu && <div className="text-[10px] text-slate-500 flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" /> {n.lieu}</div>}
                  {n.latitude && n.longitude && (
                    <a
                      href={`https://www.openstreetmap.org/?mlat=${n.latitude}&mlon=${n.longitude}#map=17/${n.latitude}/${n.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`note-gps-${n.id}`}
                      className="text-[10px] text-blue-600 hover:underline flex items-center gap-1 mt-1 font-mono"
                    >
                      <Navigation className="h-3 w-3" />
                      {n.latitude.toFixed(5)}, {n.longitude.toFixed(5)}
                      {n.location_accuracy && <span className="text-slate-400">(±{Math.round(n.location_accuracy)}m)</span>}
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {viewPhoto && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setViewPhoto(null)}>
          <img src={viewPhoto} alt="" className="max-w-full max-h-full rounded-lg" />
          <button onClick={() => setViewPhoto(null)} className="absolute top-4 right-4 bg-white rounded-full h-10 w-10 flex items-center justify-center"><X className="h-5 w-5" /></button>
        </div>
      )}
    </div>
  );
}
